import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";

type ToastType = "success" | "error" | "info";

interface MemberOption {
  id: number;
  full_name: string;
  email: string | null;
  photo: string | null;
  photo_position: string | null;
  profession: string | null;
}

interface Testimonial {
  id: number;
  member_id: number;
  text: string;
  status: string;
  source: string | null;
  published_by_admin_at: string | null;
  created_at: string;
  member_name: string;
  member_photo: string | null;
  member_photo_position: string | null;
  member_profession: string;
}

interface TestimonialManagerProps {
  onToast: (type: ToastType, message: string) => void;
}

function buildTestimonialLink(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${pathPrefix}/depoimento`;
}

function PreviewCard({
  name,
  profession,
  photo,
  photoPosition,
  text,
}: {
  name: string;
  profession: string;
  photo: string | null;
  photoPosition: string | null;
  text: string;
}) {
  return (
    <div className="group bg-beige/60 rounded-2xl p-5 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full overflow-hidden shrink-0">
          {photo ? (
            <img
              src={photo}
              alt={name}
              className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(photoPosition)]}`}
            />
          ) : (
            <MemberPhotoPlaceholder size="sm" rounded="full" />
          )}
        </div>
        <div className="min-w-0">
          <p className="font-display text-sm text-brown truncate">{name || "Integrante"}</p>
          {profession && (
            <p className="font-body text-xs text-primary/70 truncate">{profession}</p>
          )}
        </div>
      </div>
      <div className="relative flex-1">
        <i className="ri-double-quotes-l text-2xl text-primary/15 absolute -top-1 -left-1" />
        <p className="font-body text-sm text-brown/70 leading-relaxed pl-5 line-clamp-3">
          {text || "O depoimento aparecerá aqui..."}
        </p>
      </div>
    </div>
  );
}

export default function TestimonialManager({ onToast }: TestimonialManagerProps) {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [statusChoice, setStatusChoice] = useState<"approved" | "pending">("approved");
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberResults, setMemberResults] = useState<MemberOption[]>([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingForMember, setExistingForMember] = useState<string | null>(null);
  const memberSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function loadTestimonials() {
    setLoading(true);
    const { data, error } = await supabase
      .from("testimonials")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error && data) {
      const memberIds = [...new Set(data.map((t: { member_id: number }) => t.member_id))];
      const { data: membersData } = await supabase
        .from("members")
        .select("id, full_name, photo, photo_position, profession")
        .in("id", memberIds);

      const memberMap = new Map<number, { name: string; photo: string | null; photo_position: string | null; profession: string }>();
      if (membersData) {
        membersData.forEach((m: { id: string | number; full_name: string; photo: string | null; photo_position: string | null; profession: string | null }) => {
          memberMap.set(Number(m.id), {
            name: m.full_name,
            photo: m.photo,
            photo_position: m.photo_position || "top",
            profession: m.profession || "",
          });
        });
      }

      const merged: Testimonial[] = data.map((t: {
        id: number;
        member_id: number;
        text: string;
        status: string;
        source: string | null;
        published_by_admin_at: string | null;
        created_at: string;
      }) => ({
        id: t.id,
        member_id: t.member_id,
        text: t.text,
        status: t.status,
        source: t.source || null,
        published_by_admin_at: t.published_by_admin_at || null,
        created_at: t.created_at,
        member_name: memberMap.get(t.member_id)?.name || "Integrante desconhecida",
        member_photo: memberMap.get(t.member_id)?.photo || null,
        member_photo_position: memberMap.get(t.member_id)?.photo_position || null,
        member_profession: memberMap.get(t.member_id)?.profession || "",
      }));

      setTestimonials(merged);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadTestimonials();
  }, []);

  function handleMemberSearchChange(value: string) {
    setMemberSearch(value);
    if (memberSearchTimer.current) clearTimeout(memberSearchTimer.current);
    const term = value.trim().replace(/[%_,]/g, "");
    if (!term) {
      setMemberResults([]);
      return;
    }
    memberSearchTimer.current = setTimeout(async () => {
      setMemberSearching(true);
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, email, photo, photo_position, profession")
        .in("status", ["approved", "active"])
        .ilike("full_name", `%${term}%`)
        .order("full_name", { ascending: true })
        .limit(8);
      setMemberSearching(false);
      setMemberResults(!error && data ? (data as MemberOption[]) : []);
    }, 300);
  }

  function selectMember(member: MemberOption) {
    setSelectedMember(member);
    setMemberSearch("");
    setMemberResults([]);
    checkExistingTestimonial(member.id);
  }

  async function checkExistingTestimonial(memberId: number) {
    const { data } = await supabase
      .from("testimonials")
      .select("id")
      .eq("member_id", memberId)
      .maybeSingle();
    setExistingForMember(data ? String((data as { id: number }).id) : null);
  }

  function clearMember() {
    setSelectedMember(null);
    setMemberSearch("");
    setMemberResults([]);
    setExistingForMember(null);
  }

  function openCreate() {
    setEditingId(null);
    setText("");
    setStatusChoice("approved");
    setSelectedMember(null);
    setMemberSearch("");
    setMemberResults([]);
    setExistingForMember(null);
    setShowForm(true);
  }

  function openEdit(t: Testimonial) {
    setEditingId(t.id);
    setText(t.text);
    setStatusChoice(t.status === "approved" ? "approved" : "pending");
    setSelectedMember({
      id: t.member_id,
      full_name: t.member_name,
      email: null,
      photo: t.member_photo,
      photo_position: t.member_photo_position,
      profession: t.member_profession || null,
    });
    setMemberSearch("");
    setMemberResults([]);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setText("");
    setStatusChoice("approved");
    setSelectedMember(null);
    setMemberSearch("");
    setMemberResults([]);
    setExistingForMember(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMember) {
      onToast("error", "Selecione a integrante do depoimento.");
      return;
    }
    if (text.trim().length < 10) {
      onToast("error", "O depoimento precisa ter pelo menos 10 caracteres.");
      return;
    }
    if (text.trim().length > 1000) {
      onToast("error", "O depoimento deve ter no máximo 1000 caracteres.");
      return;
    }

    setSaving(true);

    if (editingId) {
      const { error } = await supabase
        .from("testimonials")
        .update({
          text: text.trim(),
          status: statusChoice,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingId);
      if (error) {
        onToast("error", "Erro ao salvar depoimento: " + error.message);
        setSaving(false);
        return;
      }
      onToast("success", "Depoimento atualizado com sucesso!");
    } else {
      const { error } = await supabase
        .from("testimonials")
        .upsert({
          member_id: selectedMember.id,
          text: text.trim(),
          status: statusChoice,
          source: "admin",
          published_by_admin_at: new Date().toISOString(),
        }, { onConflict: "member_id" });
      if (error) {
        onToast("error", "Erro ao publicar depoimento: " + error.message);
        setSaving(false);
        return;
      }
      onToast(
        "success",
        statusChoice === "approved"
          ? "Depoimento publicado em nome da integrante! Já está visível na página inicial."
          : "Depoimento salvo como pendente.",
      );
    }

    setSaving(false);
    closeForm();
    loadTestimonials();
  }

  async function toggleStatus(t: Testimonial) {
    const newStatus = t.status === "approved" ? "pending" : "approved";
    const { error } = await supabase
      .from("testimonials")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", t.id);
    if (!error) {
      setTestimonials((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, status: newStatus } : x)),
      );
      onToast(
        "success",
        newStatus === "approved"
          ? "Depoimento aprovado! Já está visível na página inicial."
          : "Depoimento movido para pendente.",
      );
    } else {
      onToast("error", "Erro ao atualizar status.");
    }
  }

  async function deleteTestimonial(t: Testimonial) {
    if (!confirm(`Tem certeza que deseja excluir o depoimento de ${t.member_name}? Esta ação não pode ser desfeita.`))
      return;
    const { error } = await supabase.from("testimonials").delete().eq("id", t.id);
    if (!error) {
      setTestimonials((prev) => prev.filter((x) => x.id !== t.id));
      onToast("success", "Depoimento excluído.");
    } else {
      onToast("error", "Erro ao excluir depoimento.");
    }
  }

  const pendingCount = testimonials.filter((t) => t.status === "pending").length;
  const approvedCount = testimonials.filter((t) => t.status === "approved").length;
  const rejectedCount = testimonials.filter((t) => t.status === "rejected").length;
  const visibleTestimonials =
    statusFilter === "all"
      ? testimonials
      : testimonials.filter((t) => t.status === statusFilter);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-display text-xl text-brown">Depoimentos</h3>
          <p className="font-body text-sm text-brown/60 mt-1">
            Gerencie os depoimentos que aparecem na página inicial da comunidade
          </p>
          {pendingCount > 0 && (
            <div className="mt-2.5 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-body text-sm">
              <i className="ri-time-line" />
              <span>
                <strong>{pendingCount}</strong> depoimento{pendingCount > 1 ? "s" : ""} aguardando aprovação
              </span>
            </div>
          )}
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" /> Adicionar depoimento em nome de
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          { key: "all" as const, label: "Todos", count: testimonials.length },
          { key: "pending" as const, label: "Pendentes", count: pendingCount },
          { key: "approved" as const, label: "Aprovados", count: approvedCount },
          { key: "rejected" as const, label: "Recusados", count: rejectedCount },
        ].map((chip) => (
          <button
            key={chip.key}
            onClick={() => setStatusFilter(chip.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
              statusFilter === chip.key
                ? "bg-primary text-white"
                : "bg-white text-brown hover:bg-beige border border-nude/60"
            }`}
          >
            <span>{chip.label}</span>
            <span className={`font-display text-base ${statusFilter === chip.key ? "text-white/80" : "text-brown/40"}`}>
              {chip.count}
            </span>
          </button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 mb-6">
          <h4 className="font-display text-lg text-brown mb-4">
            {editingId ? "Editar depoimento" : "Adicionar depoimento em nome de"}
          </h4>

          <form onSubmit={handleSave} className="space-y-5">
            {/* Integrante */}
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Integrante *
              </label>

              {editingId || selectedMember ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full overflow-hidden shrink-0">
                      {selectedMember?.photo ? (
                        <img
                          src={selectedMember.photo}
                          alt={selectedMember.full_name}
                          className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(selectedMember.photo_position)]}`}
                        />
                      ) : (
                        <MemberPhotoPlaceholder size="sm" rounded="full" />
                      )}
                    </div>
                    <div>
                      <p className="font-body text-sm text-brown">{selectedMember?.full_name}</p>
                      {selectedMember?.profession && (
                        <p className="font-body text-xs text-primary/70">{selectedMember.profession}</p>
                      )}
                    </div>
                  </div>
                  {!editingId && (
                    <button
                      type="button"
                      onClick={clearMember}
                      className="px-3 py-2 rounded-full bg-white border border-nude text-brown/60 font-body text-xs hover:bg-nude/40 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-close-line mr-1" /> Trocar
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => handleMemberSearchChange(e.target.value)}
                    placeholder="Buscar integrante por nome..."
                    className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  />
                  {memberSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    </div>
                  )}
                  {memberResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-nude rounded-xl overflow-hidden max-h-60 overflow-y-auto">
                      {memberResults.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => selectMember(m)}
                          className="w-full text-left px-4 py-2.5 font-body text-sm text-brown hover:bg-beige/60 transition-colors cursor-pointer flex items-center gap-2.5"
                        >
                          <div className="w-8 h-8 rounded-full overflow-hidden shrink-0">
                            {m.photo ? (
                              <img
                                src={m.photo}
                                alt={m.full_name}
                                className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(m.photo_position)]}`}
                              />
                            ) : (
                              <MemberPhotoPlaceholder size="sm" rounded="full" />
                            )}
                          </div>
                          <span className="flex-1 min-w-0">
                            <span className="block truncate">{m.full_name}</span>
                            {m.profession && (
                              <span className="block text-xs text-brown/50 truncate">{m.profession}</span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {memberSearch.trim() && !memberSearching && memberResults.length === 0 && (
                    <p className="font-body text-xs text-brown/40 mt-1.5">
                      Nenhuma integrante ativa encontrada com esse nome.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Texto */}
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Depoimento *
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                maxLength={1000}
                placeholder="Cole aqui o texto que a integrante enviou..."
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="font-body text-xs text-brown/40">Mínimo 10 caracteres</p>
                <p className={`font-body text-xs ${text.length > 1000 ? "text-red-500" : "text-brown/40"}`}>
                  {text.length}/1000
                </p>
              </div>
            </div>

            {/* Aviso: integrante já tem depoimento */}
            {existingForMember && !editingId && (
              <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                <i className="ri-alert-line text-amber-600 mt-0.5 shrink-0" />
                <span className="font-body text-sm text-amber-800 leading-relaxed">
                  Esta integrante já possui um depoimento. Ao publicar, o texto atual será{" "}
                  <strong>substituído</strong> pelo novo.
                </span>
              </div>
            )}

            {/* Status */}
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Status
              </label>
              <select
                value={statusChoice}
                onChange={(e) => setStatusChoice(e.target.value as "approved" | "pending")}
                className="px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors cursor-pointer"
              >
                <option value="approved">Aprovado — visível no site</option>
                <option value="pending">Pendente — aguardando aprovação</option>
              </select>
              {!editingId && (
                <p className="font-body text-xs text-brown/50 mt-1.5 leading-relaxed">
                  Por padrão o depoimento entra já aprovado. Escolha "pendente" se quiser revisar antes.
                </p>
              )}
            </div>

            {/* Preview */}
            {(text.trim() || selectedMember) && (
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Prévia no site
                </label>
                <div className="max-w-sm">
                  <PreviewCard
                    name={selectedMember?.full_name || "Integrante"}
                    profession={selectedMember?.profession || ""}
                    photo={selectedMember?.photo || null}
                    photoPosition={selectedMember?.photo_position || null}
                    text={text.trim()}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> Salvando...
                  </>
                ) : editingId ? (
                  "Salvar alterações"
                ) : (
                  "Publicar depoimento"
                )}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-6 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3" />
          <p className="font-body text-sm text-brown/60">Carregando depoimentos...</p>
        </div>
      ) : visibleTestimonials.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <i className="ri-chat-quote-line text-4xl text-brown/20 mb-4" />
          <p className="font-body text-brown/60">
            {testimonials.length === 0 ? "Nenhum depoimento cadastrado ainda." : "Nenhum depoimento para este filtro."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleTestimonials.map((t) => {
            const isPending = t.status === "pending";
            const isApproved = t.status === "approved";
            const isRejected = t.status === "rejected";
            const isAdminSource = t.source === "admin";

            return (
              <div key={t.id} className={`bg-white rounded-2xl p-5 md:p-6 ${isRejected ? "opacity-60" : ""}`}>
                <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
                  {/* Member photo */}
                  <div className="w-14 h-14 rounded-full overflow-hidden shrink-0">
                    {t.member_photo ? (
                      <img
                        src={t.member_photo}
                        alt={t.member_name}
                        className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(t.member_photo_position)]}`}
                      />
                    ) : (
                      <MemberPhotoPlaceholder size="md" rounded="full" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h4 className="font-display text-base text-brown">{t.member_name}</h4>
                      {t.member_profession && (
                        <span className="font-body text-sm text-primary">{t.member_profession}</span>
                      )}
                      {isPending && (
                        <span className="px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-body text-xs uppercase tracking-wide">
                          Pendente
                        </span>
                      )}
                      {isApproved && (
                        <span className="px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 font-body text-xs uppercase tracking-wide">
                          Aprovado
                        </span>
                      )}
                      {isRejected && (
                        <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 font-body text-xs uppercase tracking-wide">
                          Rejeitado
                        </span>
                      )}
                    </div>

                    {/* Origin badge — só no admin */}
                    <div className="mb-2">
                      {isAdminSource ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-body text-xs">
                          <i className="ri-user-star-line" />
                          Publicado pela admin
                          {t.published_by_admin_at && (
                            <span className="text-accent-700/60">
                              em {new Date(t.published_by_admin_at).toLocaleDateString("pt-BR")}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-beige text-brown/60 font-body text-xs">
                          <i className="ri-user-line" />
                          Escrito pela integrante
                        </span>
                      )}
                    </div>

                    <div className="relative mt-1">
                      <i className="ri-double-quotes-l text-xl text-primary/15 absolute -top-1 -left-1" />
                      <p className="font-body text-sm text-brown/70 leading-relaxed pl-5 whitespace-pre-wrap">
                        {t.text}
                      </p>
                    </div>

                    <p className="font-body text-xs text-brown/40 mt-3">
                      Enviado em {new Date(t.created_at).toLocaleString("pt-BR")}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(buildTestimonialLink());
                        onToast("success", "Link copiado! Envie para uma integrante deixar o depoimento dela.");
                      }}
                      className="px-3 py-2 rounded-lg bg-accent-100 text-accent-700 font-body text-xs hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                      title="Copiar link do depoimento"
                    >
                      <i className="ri-link mr-1" /> Copiar link
                    </button>
                    <button
                      onClick={() => openEdit(t)}
                      className="px-3 py-2 rounded-lg bg-primary/10 text-primary font-body text-xs hover:bg-primary/20 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-edit-line mr-1" /> Editar
                    </button>
                    <button
                      onClick={() => toggleStatus(t)}
                      className={`px-3 py-2 rounded-lg font-body text-xs transition-colors whitespace-nowrap cursor-pointer ${
                        isApproved
                          ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                          : "bg-green-100 text-green-700 hover:bg-green-200"
                      }`}
                    >
                      {isApproved ? (
                        <>
                          <i className="ri-time-line mr-1" /> Marcar pendente
                        </>
                      ) : (
                        <>
                          <i className="ri-check-line mr-1" /> Aprovar
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => deleteTestimonial(t)}
                      className="px-3 py-2 rounded-lg bg-white border border-nude text-red-500 font-body text-xs hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}